import { useQuery } from '@apollo/client/react';
import {
  Flex,
  RadioGroup,
  RadioTile,
  Select,
  SelectItem,
  TextInput,
} from '@utilitywarehouse/hearth-react';
import { FormikProps, useFormikContext } from 'formik';
import { ChangeEvent, useEffect } from 'react';

import { AddressForm } from '@features/Address/AddressPicker';
import { formatAddress } from '@features/Address/formatAddress';
import { BundleBasketFragment } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { BooleanSelection } from '@src/types/index';
import { Title, YourDetailsFormValues } from '@src/types/yourDetails';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupRecentMoveSelectedOnce } from '@tracking/trackingEvents';
import { getBundleBasket } from '@utils/queries/getBasket';

import { DoBField } from '../DoBField';
import '../styles.scss';

const personalTitles = Object.values(Title).map((title) => ({
  label: title,
  value: title,
}));

const AboutYou = () => {
  const trackRecentMoveSelected = useTrackSignupRecentMoveSelectedOnce();
  const { trackingContexts } = useTrackingContext();

  const { basketId } = useBasketContext();

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
    setFieldValue,
  }: FormikProps<YourDetailsFormValues> = useFormikContext();

  const { setDetailsItem } = useDetailsContext();
  const { data: basketData } = useQuery(getBundleBasket, {
    variables: { basketId },
    fetchPolicy: 'cache-and-network',
  });

  const getFieldProps = useFieldProps<YourDetailsFormValues>({
    values,
    errors,
    handleChange: (event: ChangeEvent<HTMLInputElement>) => {
      handleChange(event);
      setDetailsItem(
        event.target.name as keyof YourDetailsFormValues,
        event.target.value
      );
    },
    handleBlur,
    touched,
    submitCount,
  });

  const basket = basketData?.basket as BundleBasketFragment;

  useEffect(() => {
    if (values.aboutAddressRecent === BooleanSelection.Yes) {
      trackRecentMoveSelected({}, { contexts: trackingContexts });
    }
  }, [values.aboutAddressRecent, trackRecentMoveSelected, trackingContexts]);

  return (
    <Flex direction="column" spacing="xl">
      <Select
        {...getFieldProps('aboutNameTitle')}
        label="Title"
        className="details__formInput"
        autoComplete="honorific-prefix"
        data-testid="aboutNameTitle"
        onValueChange={(val) => {
          setFieldValue('aboutNameTitle', val);
          setDetailsItem('aboutNameTitle', val as Title);
        }}
        required
      >
        {personalTitles.map(({ label, value }) => (
          <SelectItem value={value} key={value}>
            {label}
          </SelectItem>
        ))}
      </Select>

      <TextInput
        {...getFieldProps('aboutNameFirst')}
        className="details__formInput"
        label="First name"
        data-testid="aboutNameFirst"
        required
      />
      <TextInput
        {...getFieldProps('aboutNameLast')}
        className="details__formInput"
        label="Last name"
        data-testid="aboutNameLast"
        required
      />
      <DoBField
        {...getFieldProps('aboutDoB')}
        className="details__formInput"
        onChange={(value) => {
          setDetailsItem('aboutDoB', value);
          setFieldValue('aboutDoB', value);
        }}
        label="Date of birth"
        data-testid="aboutDoB"
        required
      />
      <TextInput
        {...getFieldProps('aboutContactNumber')}
        className="details__formInput"
        type="tel"
        label="Contact number"
        data-testid="aboutContactNumber"
        required
      />
      <TextInput
        {...getFieldProps('aboutEmail')}
        className="details__formInput"
        type="email"
        label="Email address"
        data-testid="aboutEmail"
        id="aboutEmail"
        required
      />
      <Flex spacing="xl" direction="column">
        <RadioGroup
          {...getFieldProps('aboutAddressRecent')}
          direction="row"
          label={`Did you move into ${formatAddress(
            basket?.details?.supplyAddress || {}
          )} within the last six months?`}
          className="details__radioField"
          data-testid="aboutAddressRecent"
          onValueChange={(val) => setFieldValue('aboutAddressRecent', val)}
        >
          <RadioTile
            data-testid="aboutAddressRecentYes"
            value={BooleanSelection.Yes}
            label="Yes"
            id="aboutAddressRecentYes"
          />
          <RadioTile
            data-testid="aboutAddressRecentNo"
            value={BooleanSelection.No}
            label="No"
            id="aboutAddressRecentNo"
          />
        </RadioGroup>

        {values.aboutAddressRecent === BooleanSelection.Yes && (
          <RadioGroup
            {...getFieldProps('aboutAddressPreviousOwner')}
            label="Did you own your previous home?"
            className="details__radioField"
            direction="row"
            onValueChange={(val) =>
              setFieldValue('aboutAddressPreviousOwner', val)
            }
          >
            <RadioTile
              data-testid="aboutAddressPreviousOwnerYes"
              value={BooleanSelection.Yes}
              label="Yes"
              id="aboutAddressPreviousOwnerYes"
            />
            <RadioTile
              data-testid="aboutAddressPreviousOwnerNo"
              value={BooleanSelection.No}
              label="No"
              id="aboutAddressPreviousOwnerNo"
            />
          </RadioGroup>
        )}

        {values.aboutAddressPreviousOwner === BooleanSelection.Yes &&
          values.aboutAddressRecent === BooleanSelection.Yes && (
            <AddressForm
              className="details__formInput"
              name="aboutPreviousAddress"
              isLoading={false}
              onChange={(selectedAddress) => {
                setDetailsItem(
                  'aboutPreviousAddress',
                  selectedAddress as never
                );
              }}
              data-testid="aboutPreviousAddress"
            />
          )}
      </Flex>
    </Flex>
  );
};

export { AboutYou };
